'use strict';

import {
    parseSwissMoney,
    parseSwissInteger,
    formatSwissMoney,
    formatSwissInteger,
    countSignificantCharsBeforeCaret,
    mapCaretAfterFormat,
    formatSwissMoneyLive,
    formatSwissIntegerLive,
} from './swiss-number-format.mjs';

/**
 * Comportement de saisie suisse générique pour les champs prix/km.
 *
 * Marquage attendu côté Fluid :
 *   - data-swiss-number-input="money" (montant CHF)
 *   - data-swiss-number-input="km" (entier)
 * Option formulaire : data-swiss-number-submit-normalize — canonique au submit
 * puis réaffichage groupé si la page reste (validation).
 * Messages optionnels :
 *   - data-swiss-number-error-money
 *   - data-swiss-number-error-integer
 *   - data-swiss-number-allow-negative="1" (montants signés : équipements)
 *
 * Stratégie : formatage live sur `input` avec préservation du caret ;
 * focus reste groupé ; blur applique le format final si parse OK ;
 * composition IME : pas de réécriture avant compositionend ;
 * submit : bloque si valeur non vide invalide + toast local (pas de refresh).
 */

const INPUT_SELECTOR = '[data-swiss-number-input]';
const INPUT_BOUND_FLAG = 'swissNumberBound';
const FORM_BOUND_FLAG = 'swissNumberSubmitBound';
const SUBMIT_NORMALIZE_ATTRIBUTE = 'data-swiss-number-submit-normalize';
const COMPOSING_FLAG = 'swissNumberComposing';
const CLIENT_FLASH_ID = 'swiss-number-client-flash';

/**
 * Défauts selon la langue document (formulaires sans data-swiss-number-error-*).
 *
 * @returns {{money: string, integer: string}}
 */
function defaultErrorMessages() {
    const lang = (document.documentElement.lang || 'fr').toLowerCase();
    if (lang.startsWith('de')) {
        return { money: 'Ungültige Zahl.', integer: 'Ungültige Ganzzahl.' };
    }
    if (lang.startsWith('en')) {
        return { money: 'Invalid number.', integer: 'Invalid whole number.' };
    }
    return { money: 'Nombre invalide.', integer: 'Nombre entier invalide.' };
}

/**
 * Échappe un id CSS (fallback si CSS.escape absent).
 *
 * @param {string} value
 * @returns {string}
 */
function escapeCssIdent(value) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        return CSS.escape(value);
    }
    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}

/**
 * @param {HTMLInputElement} input
 * @returns {boolean}
 */
function isMoneyField(input) {
    return input.getAttribute('data-swiss-number-input') === 'money';
}

/**
 * @param {HTMLInputElement} input
 * @returns {boolean}
 */
function allowsNegativeMoney(input) {
    return isMoneyField(input) && input.getAttribute('data-swiss-number-allow-negative') === '1';
}

/**
 * Affiche la valeur groupée (chargement / blur). Invalide → inchangé.
 * Point/virgule final transitoire (ex. 1'234.) : retiré avant parse pour
 * produire un affichage final cohérent.
 *
 * @param {HTMLInputElement} input
 */
function applyGroupedDisplay(input) {
    if (input.value.trim() === '') {
        return;
    }

    // Retire un séparateur décimal orphelin en fin de saisie.
    const rawForParse = input.value.trim().replace(/[.,]$/, '');

    if (isMoneyField(input)) {
        const parsed = parseSwissMoney(rawForParse, allowsNegativeMoney(input));
        if (parsed === null) {
            return;
        }
        input.value = formatSwissMoney(parsed, true);
        return;
    }

    const parsedInteger = parseSwissInteger(rawForParse);
    if (parsedInteger === null) {
        return;
    }
    input.value = formatSwissInteger(parsedInteger);
}

/**
 * Valeur canonique pour soumission.
 * Si parse strict échoue, tente un nettoyage des séparateurs (défense submit).
 *
 * @param {HTMLInputElement} input
 * @returns {string}
 */
function canonicalValue(input) {
    if (input.value.trim() === '') {
        return '';
    }

    const rawForParse = input.value.trim().replace(/[.,]$/, '');
    const allowNegative = allowsNegativeMoney(input);

    if (isMoneyField(input)) {
        const parsed = parseSwissMoney(rawForParse, allowNegative);
        if (parsed !== null) {
            return String(parsed);
        }
        // Best-effort : digits + au plus 2 décimales après strip.
        const cleaned = rawForParse
            .replace(/['\u2019\u0020\u00A0\u202F\u2009]/g, '')
            .replace(',', '.');
        const pattern = allowNegative ? /^-?\d+(\.\d{1,2})?$/ : /^\d+(\.\d{1,2})?$/;
        return pattern.test(cleaned) ? cleaned : input.value;
    }

    const parsedInteger = parseSwissInteger(rawForParse);
    if (parsedInteger !== null) {
        return String(parsedInteger);
    }
    const cleanedInteger = rawForParse.replace(/['\u2019\u0020\u00A0\u202F\u2009]/g, '');
    return /^\d+$/.test(cleanedInteger) ? cleanedInteger : input.value;
}

/**
 * Vide = OK ; non vide doit parser strictement.
 *
 * @param {HTMLInputElement} input
 * @returns {boolean}
 */
function isStrictlyValidInput(input) {
    if (input.value.trim() === '') {
        return true;
    }

    const rawForParse = input.value.trim().replace(/[.,]$/, '');
    if (isMoneyField(input)) {
        return parseSwissMoney(rawForParse, allowsNegativeMoney(input)) !== null;
    }

    return parseSwissInteger(rawForParse) !== null;
}

/**
 * Libellé voisin pour contextualiser le toast (label[for] puis name).
 *
 * @param {HTMLInputElement} input
 * @returns {string}
 */
function resolveFieldLabel(input) {
    if (input.id) {
        const label = document.querySelector(`label[for="${escapeCssIdent(input.id)}"]`);
        if (label) {
            const text = (label.textContent || '').replace(/\s+/g, ' ').trim();
            if (text !== '') {
                return text;
            }
        }
    }

    return (input.getAttribute('name') || '').trim();
}

/**
 * Message d’erreur localisé (data-* formulaire) + champ si connu.
 *
 * @param {HTMLFormElement} form
 * @param {HTMLInputElement} input
 * @returns {string}
 */
function buildValidationMessage(form, input) {
    const defaults = defaultErrorMessages();
    const moneyMsg = form.getAttribute('data-swiss-number-error-money') || defaults.money;
    const integerMsg = form.getAttribute('data-swiss-number-error-integer') || defaults.integer;
    const base = isMoneyField(input) ? moneyMsg : integerMsg;
    const fieldLabel = resolveFieldLabel(input);

    return fieldLabel !== '' ? `${base} (${fieldLabel})` : base;
}

/**
 * Agrège les messages de tous les champs invalides (séparés par « · »).
 *
 * @param {HTMLFormElement} form
 * @param {HTMLInputElement[]} invalidInputs
 * @returns {string}
 */
function buildValidationMessageForAll(form, invalidInputs) {
    return invalidInputs.map((input) => buildValidationMessage(form, input)).join(' · ');
}

/**
 * Retire le toast client s’il est présent.
 */
function clearClientFlash() {
    const item = document.getElementById(CLIENT_FLASH_ID);
    if (item) {
        item.remove();
    }
}

/**
 * Toast local dans la zone flash BO (sans round-trip).
 *
 * @param {string} message
 */
function showClientFlash(message) {
    let list = document.querySelector('.vehicle-backend-flashmessages');
    if (!(list instanceof HTMLElement)) {
        const shell = document.querySelector('.cc-backoffice-shell');
        const main = document.querySelector('.cc-backoffice-shell__content');
        list = document.createElement('ul');
        list.className = 'vehicle-backend-flashmessages';
        list.setAttribute('role', 'alert');
        list.setAttribute('aria-live', 'assertive');
        if (shell instanceof HTMLElement && main instanceof HTMLElement && main.parentNode === shell) {
            shell.insertBefore(list, main);
        } else if (shell instanceof HTMLElement) {
            shell.prepend(list);
        } else {
            document.body.prepend(list);
        }
    }

    let item = list.querySelector(`#${CLIENT_FLASH_ID}`);
    if (!(item instanceof HTMLElement)) {
        item = document.createElement('li');
        item.id = CLIENT_FLASH_ID;
        item.className = 'vehicle-backend-flashmessages__item vehicle-backend-flashmessages__item--warning';
        list.prepend(item);
    }

    item.setAttribute('role', 'alert');
    item.innerHTML = '';
    const text = document.createElement('span');
    text.className = 'vehicle-backend-flashmessages__text';
    text.textContent = message;
    item.appendChild(text);
    item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

/**
 * Reformate live en conservant le caret (chiffres + décimal significatifs).
 *
 * @param {HTMLInputElement} input
 * @param {InputEvent|Event} [event]
 */
function applyLiveFormat(input, event) {
    if (input.dataset[COMPOSING_FLAG] === '1') {
        return;
    }
    if (event && event.isComposing) {
        return;
    }

    const previous = input.value;
    const caretPos = input.selectionStart ?? previous.length;
    const significantCount = countSignificantCharsBeforeCaret(previous, caretPos);

    const formatted = isMoneyField(input)
        ? formatSwissMoneyLive(previous, allowsNegativeMoney(input))
        : formatSwissIntegerLive(previous);

    // null = invalide → laisser la chaîne telle quelle (ne pas annuler la frappe).
    if (formatted === null || formatted === previous) {
        return;
    }

    input.value = formatted;
    const nextCaret = mapCaretAfterFormat(formatted, significantCount);
    try {
        input.setSelectionRange(nextCaret, nextCaret);
    } catch (_error) {
        // Certains types d'input n'autorisent pas setSelectionRange.
    }
}

/**
 * @param {HTMLInputElement} input
 */
function bindInput(input) {
    if (input.dataset[INPUT_BOUND_FLAG] === '1') {
        return;
    }
    input.dataset[INPUT_BOUND_FLAG] = '1';

    applyGroupedDisplay(input);

    input.addEventListener('compositionstart', () => {
        input.dataset[COMPOSING_FLAG] = '1';
    });
    input.addEventListener('compositionend', () => {
        input.dataset[COMPOSING_FLAG] = '0';
        applyLiveFormat(input);
    });

    input.addEventListener('input', (event) => {
        clearClientFlash();
        applyLiveFormat(input, event);
    });
    input.addEventListener('blur', () => applyGroupedDisplay(input));
    // Focus : pas de dégroupage — la valeur reste groupée et éditable.
}

/**
 * @param {HTMLFormElement} form
 */
function bindFormSubmitNormalization(form) {
    if (form.dataset[FORM_BOUND_FLAG] === '1') {
        return;
    }
    form.dataset[FORM_BOUND_FLAG] = '1';

    form.addEventListener('submit', (event) => {
        /** @type {HTMLInputElement[]} */
        const boundInputs = [];
        /** @type {HTMLInputElement[]} */
        const invalidInputs = [];

        form.querySelectorAll(INPUT_SELECTOR).forEach((node) => {
            if (!(node instanceof HTMLInputElement)) {
                return;
            }
            boundInputs.push(node);
            if (!isStrictlyValidInput(node)) {
                invalidInputs.push(node);
            }
        });

        // Bloque le POST tant qu’un montant/km non vide est illisible.
        if (invalidInputs.length > 0) {
            event.preventDefault();
            event.stopPropagation();
            showClientFlash(buildValidationMessageForAll(form, invalidInputs));
            try {
                invalidInputs[0].focus();
            } catch (_error) {
                // Ignore focus refusés (champs disabled / hors viewport).
            }
            return;
        }

        clearClientFlash();
        boundInputs.forEach((input) => {
            // Canonique synchronement pour que le navigateur sérialise sans apostrophe.
            input.value = canonicalValue(input);
        });
        // Re-group après la sérialisation (page reste en cas d'erreur validation).
        window.setTimeout(() => {
            boundInputs.forEach((input) => applyGroupedDisplay(input));
        }, 0);
    });
}

/**
 * Initialise les champs suisses présents dans le document (idempotent).
 *
 * @param {Document|HTMLElement} [documentRoot=document]
 */
export function init(documentRoot = document) {
    const inputs = documentRoot.querySelectorAll(INPUT_SELECTOR);
    /** @type {Set<HTMLFormElement>} */
    const formsToNormalize = new Set();

    inputs.forEach((input) => {
        if (!(input instanceof HTMLInputElement)) {
            return;
        }
        bindInput(input);
        if (input.form instanceof HTMLFormElement && input.form.hasAttribute(SUBMIT_NORMALIZE_ATTRIBUTE)) {
            formsToNormalize.add(input.form);
        }
    });

    formsToNormalize.forEach((form) => bindFormSubmitNormalization(form));
}

export default { init };
